import { useState } from 'react';
import styled from 'styled-components';
import { ChevronRight, MessageSquarePlus } from 'lucide-react';
import { useSupportWidgetStore } from '../../../../stores/supportWidgetStore';
import { useMySupportRequests } from '../../../../hooks/useSupport';
import { RequestCard } from '../RequestCard';
import { popularArticles as getPopularArticles, searchHelp } from '../help/helpContent';
import {
  ArticleItem,
  SearchWrapper as BaseSearchWrapper,
  SearchIcon,
  SearchInput,
  SectionHead,
} from '../supportShared';
import { useGoToHelpArticle } from '../useGoToHelpArticle';

const Pad = styled.div`
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[4]} 0;
`;

const ActionCard = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  width: 100%;
  text-align: left;
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.base};
  }
`;

const ActionIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.backgrounds.light};
  color: ${({ theme }) => theme.colors.primary.base};
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ActionText = styled.div`
  flex: 1;
  min-width: 0;
`;

const ActionTitle = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ActionSub = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
  margin-top: 2px;
`;

// HomeTab sits directly under the "Send us a message" card, so the search box
// needs breathing room above it that HelpTab's (top-of-panel) copy doesn't.
const SearchWrapper = styled(BaseSearchWrapper)`
  margin-top: ${({ theme }) => theme.spacing[3]};
`;

const ArticleTitle = styled.div`
  flex: 1;
  min-width: 0;
`;

const BottomSpacer = styled.div`
  height: ${({ theme }) => theme.spacing[4]};
`;

export function HomeTab() {
  const goToCompose = useSupportWidgetStore((s) => s.goToCompose);
  const openThread = useSupportWidgetStore((s) => s.openThread);
  const goToArticle = useGoToHelpArticle();
  // Only the single most recent request is shown here — no need to pull a full page.
  const { data: requestsData } = useMySupportRequests({ limit: 1 });
  const [query, setQuery] = useState('');

  const mostRecent = requestsData?.pages[0]?.data[0];
  const searchResults = searchHelp(query);
  const popularArticles = getPopularArticles(3);

  return (
    <Pad>
      <ActionCard type="button" onClick={() => goToCompose()}>
        <ActionIcon>
          <MessageSquarePlus />
        </ActionIcon>
        <ActionText>
          <ActionTitle>Send us a message</ActionTitle>
          <ActionSub>Ask a question or report a problem</ActionSub>
        </ActionText>
        <ChevronRight size={16} />
      </ActionCard>

      <SearchWrapper>
        <SearchIcon aria-hidden="true" />
        <SearchInput
          type="search"
          placeholder="Search for help"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search for help"
        />
      </SearchWrapper>

      {query.trim() ? (
        <>
          <SectionHead>Search results</SectionHead>
          {searchResults.length === 0 && <ActionSub>No articles matched &quot;{query}&quot;.</ActionSub>}
          {searchResults.map((a) => (
            <ArticleItem key={a.id} onClick={() => goToArticle(a.id)}>
              <ArticleTitle>{a.title}</ArticleTitle>
              <ChevronRight size={14} />
            </ArticleItem>
          ))}
        </>
      ) : (
        <>
          {mostRecent && (
            <>
              <SectionHead>Recent message</SectionHead>
              <RequestCard request={mostRecent} onClick={() => openThread(mostRecent.id)} />
            </>
          )}

          <SectionHead>Popular articles</SectionHead>
          {popularArticles.map((a) => (
            <ArticleItem key={a.id} onClick={() => goToArticle(a.id)}>
              <ArticleTitle>{a.title}</ArticleTitle>
              <ChevronRight size={14} />
            </ArticleItem>
          ))}
        </>
      )}

      <BottomSpacer />
    </Pad>
  );
}

export default HomeTab;
